import React, { createRef, Component, ReactNode, SyntheticEvent } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import intl from 'react-intl-universal';
import { inject, observer } from 'mobx-react';
import classnames from 'classnames';
import { onClickOutside } from 'components/common/onClickOutside';

import closeIcon from 'assets/images/close.svg';
import backIcon from 'assets/images/back.svg';
import logOutIcon from 'assets/images/logout.svg';

import { UIStore } from 'locales/UIStore';

import './MyAccountWindow.scss';
import { LoginStore } from 'user/view/LoginStore';
import { Locales } from 'utils/enums';
import { LANGUAGES } from 'utils/constants';
import { handler } from 'utils/handled';

const ANIMATION_TIMEOUT = 200;

interface HeaderNavigationLink {
  name: string;
  url: string;
  dropdown?: boolean;
  submenuItems?: Array<HeaderNavigationLink>;
}
enum Screen {
  MAIN,
  LOCALE,
  FONTS
}

interface MyAccountWindowProps {
  uiStore?: UIStore;
  loginStore?: LoginStore;
  navigation: Array<HeaderNavigationLink>;
  onLogIn: () => void;
  openKeyboardModal: () => void;
  /**
   * Lets AppHeader hold a ref to this window's root element, so its CSSTransition can be
   * given a nodeRef instead of resolving the node with findDOMNode. Optional: when it is not
   * supplied the component falls back to its own ref and behaves exactly as before.
   */
  outerRef?: React.RefObject<HTMLDivElement | null>;
  closeMyAccountWindow(e: SyntheticEvent): void;
}

interface IMyAccountWindowState {
  currentScreen: Screen;
}


@inject('uiStore', 'loginStore')
@observer
class MyAccountWindow extends Component<MyAccountWindowProps, IMyAccountWindowState> {
  private ownContainerRef = React.createRef<HTMLDivElement>();
  /**
   * One ref per key. TransitionGroup keeps the outgoing child mounted while the incoming one
   * enters, so two CSSTransitions are live at once during a change and a single shared ref
   * would be overwritten by whichever rendered last — animating the wrong node on exit.
   */
  private nodeRefs = new Map<string, React.RefObject<HTMLDivElement | null>>();

  public state = {
    currentScreen: Screen.MAIN,
  };

  // One node, reachable from here and from AppHeader. The height measuring below and the
  // CSSTransition above both need it, and neither should own a separate ref to it.
  private get containerRef(): React.RefObject<HTMLDivElement | null> {
    return this.props.outerRef || this.ownContainerRef;
  }

  private goToMainScreen = () => {
    this.setState({ currentScreen: Screen.MAIN });
  }

  private goToLocaleScreen = () => {
    this.setState({ currentScreen:Screen.LOCALE });
  }

  private goToFontsScreen = () => {
    this.setState({ currentScreen:Screen.FONTS });
  }

  private logOut = () => {
    const { loginStore } = this.props;
    loginStore!.logOut();
  }

  private renderLocaleChooserIfNeeded() {
    const { uiStore, loginStore } = this.props;
    return loginStore!.currentUser
    && (
      <ul className="MyAccountWindow__list MyAccountWindow__list_separated">
        <li className="MyAccountWindow__item" onClick={this.goToLocaleScreen}>
          <a href="#" className="MyAccountWindow__itemText">
            {intl.get('header.Change Language')} (<span style={{ textTransform: 'uppercase' }}>{uiStore!.currentLocale})</span>
          </a>
        </li>
      </ul>
    );
  }

  private handleElementExit = () => {
    const rect = this.containerRef.current!.getBoundingClientRect();
    this.containerRef.current!.style.height = `${rect.height}px`;
  }

  // Supplying nodeRef changes these signatures: react-transition-group stops passing the
  // node, because the caller already holds a ref to it. The entering node now comes from the
  // ref captured for that specific transition in renderAnimationWrapper.
  private handleElementEntering = (node: HTMLElement | null) => {
    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();
    this.containerRef.current!.style.height = `${rect.height}px`;
  }

  private handleElementEntered = () => {
    this.containerRef.current!.style.height = 'auto';
  }

  /**
   * Keyed by name, not url. Every dropdown entry carries `url: '#'` — the anchor is a
   * button in all but markup — so keying on url gave several siblings the same key and
   * React was free to duplicate or drop them. AppHeader renders the same navigation array
   * and has always keyed it by name.
   */
  private renderNavigation = (link: HeaderNavigationLink) => {
    if (link.dropdown) {
      const renderSubMenuSubMenu = (item: HeaderNavigationLink) => (
        <li key={item.name} className={'AppHeader__dropdownItem__subItem'}>
          <a href={item.url} title={item.name} role="button">{item.name}</a>
        </li>
      );
      const renderSubmenu = (item: HeaderNavigationLink) => {
        if (item.dropdown) {
          let maxLength = 0;
          if (item.submenuItems) {
            item.submenuItems.forEach((element) => {
              if (element.name.length > maxLength) {
                maxLength = element.name.length;
              }
            });
          }
          const classInside = 'AppHeader__dropdownItem__subMenuMobile';
          return (
            <li key={item.name} className={'MyAccountWindow__itemText activeMobileSubMenu'}>
              <a href={item.url} title={item.name} role="button" className="identSubMenu">{item.name}</a>
              <ul className={classInside}>
                {item.submenuItems!.map(renderSubMenuSubMenu)}
              </ul>
            </li>
          );
        }
        return (
          <li key={item.name} className={'MyAccountWindow__link'}>
            <a href={item.url} title={item.name} role="button">{item.name}</a>
          </li>
        );
      };
      return (
        <li key={link.name} className="MyAccountWindow__item">
          <div className="MyAccountWindow__itemText">
            <a href={link.url} className="MyAccountWindow__link drowDown">{link.name}</a>
          </div>
          <div className="MyAccountWindow__submenu">
            <ul>
              {link.submenuItems!.map(renderSubmenu)}
            </ul>
          </div>
        </li>
      );
    }
    return (
      <li key={link.name} className="MyAccountWindow__item">
        <div className="MyAccountWindow__itemText">
          <a href={link.url} className="MyAccountWindow__link drowDown">{link.name}</a>
        </div>
      </li>
    );
  }

  private renderLogOutButtonIfNeeded() {
    return this.props.loginStore!.currentUser
    && (
      <ul className="MyAccountWindow__list">
        <li className="MyAccountWindow__item" onClick={this.logOut}>
          <img className="MyAccountWindow__itemImage" src={logOutIcon} alt={intl.get('header.Log out')} />
          <a href="#" className="MyAccountWindow__itemText">
            {intl.get('header.Log out')}
          </a>
        </li>
      </ul>
    );
  }

  private renderLogInButtonIfNeeded() {
    return !this.props.loginStore!.currentUser
      && (
        <ul className="MyAccountWindow__list">
          <li className="MyAccountWindow__item" onClick={this.props.onLogIn}>
            <img className="MyAccountWindow__itemImage" src={logOutIcon} alt={intl.get('header.Log out')} />
            <a href="#" className="MyAccountWindow__itemText">
              {intl.get('header.Log in')}
            </a>
          </li>
        </ul>
      );
  }

  private renderUserNameIfPossible() {
    const { loginStore, closeMyAccountWindow } = this.props;
    return loginStore!.currentUser
    && (
      <ul className="MyAccountWindow__list MyAccountWindow__list_separated">
        <li className="MyAccountWindow__item">
          <div className="MyAccountWindow__itemText_selected MyAccountWindow__title">
            {loginStore!.currentUser && loginStore!.currentUser.name}
          </div>
        </li>

        <a href="#" className="MyAccountWindow__closeButton" onClick={closeMyAccountWindow}><img src={closeIcon} alt="Close"  /></a>
      </ul>
    );
  }

  private renderChangeFontSize() {
    const { loginStore } = this.props;
    return loginStore!.currentUser
    && (
      <ul className="MyAccountWindow__list MyAccountWindow__list_separated">
        <li className="MyAccountWindow__item"  onClick={this.goToFontsScreen}>
          <a href="#" className="MyAccountWindow__itemText">
            {intl.get('header.change_font_size')}
          </a>
        </li>
      </ul>
    );
  }

  private renderMainScreen = () => {
    const { navigation } = this.props;
    return (
      <>
        {this.renderUserNameIfPossible()}
        <ul className="MyAccountWindow__list MyAccountWindow__list_separated MyAccountWindow__list_mobile">
          {navigation.map(this.renderNavigation)}
          <li className="MyAccountWindow__item">
            <div className="MyAccountWindow__itemText">
              <a href="#" className="MyAccountWindow__link drowDown">{intl.get('header.About')}</a>
            </div>
            <div className="MyAccountWindow__submenu">
              <ul>
                <li className={'MyAccountWindow__link'}>
                  <a href={`${process.env.REACT_APP_WP_URL}/hva-er-skolerom`} title={intl.get('header.About skolerom')} target="_blank" role="button" rel="noreferrer">{intl.get('header.About skolerom')}</a>
                </li>
                <li className={'MyAccountWindow__link'}>
                  <a href={`${process.env.REACT_APP_WP_URL}/kontakt-oss`} title={intl.get('header.contact')} target="_blank" role="button" rel="noreferrer">{intl.get('header.contact')}</a>
                </li>
                <li className={'MyAccountWindow__link'}>
                  <a href={`${process.env.REACT_APP_WP_URL}/support-skolerom`} title={intl.get('generals.support')} target="_blank" role="button" rel="noreferrer">{intl.get('generals.support')}</a>
                </li>
              </ul>
            </div>
          </li>
        </ul>
        {this.renderLocaleChooserIfNeeded()}
        {this.renderChangeFontSize()}
        <ul className="MyAccountWindow__list MyAccountWindow__list_separated">
          <li className="MyAccountWindow__item">
            <a href="#" className="MyAccountWindow__itemText" onClick={this.props.openKeyboardModal} title={intl.get('header.title.keyboard')}>{intl.get('generals.keyboard')}</a>
          </li>
        </ul>
        {this.renderLogOutButtonIfNeeded()}
        {this.renderLogInButtonIfNeeded()}
      </>
    );
  }

  private renderLanguageChangeScreen = () => {
    const { uiStore, closeMyAccountWindow, loginStore } = this.props;
    const setCurrentLocale = async (language: Locales) => {
      if (uiStore!.currentLocale === language) {
        return null;
      }
      uiStore!.setCurrentLocale(language);
      await loginStore!.getLocaleData(language);
      window.location.reload();
    };

    const renderLanguage = (language: { description: string, shortName: Locales }) => (
      <li
        className="MyAccountWindow__item"
        onClick={handler(async () => setCurrentLocale(language.shortName))}
        key={language.description}
      >
        <a
          href="#"
          className={classnames('MyAccountWindow__itemText', { MyAccountWindow__itemText_selected: uiStore!.currentLocale === language.shortName })}
        >
          {language.description} {uiStore!.currentLocale === language.shortName && `(${intl.get('header.selected')})`}
        </a>
      </li>
    );

    return (
      <>
        <ul className="MyAccountWindow__list MyAccountWindow__list_separated">
          <li className="MyAccountWindow__item MyAccountWindow__item_centered">
            <img className="MyAccountWindow__backButton" src={backIcon} alt={intl.get('header.Back')} onClick={this.goToMainScreen} />

            <div className="MyAccountWindow__itemText MyAccountWindow__itemText_selected">
              {intl.get('header.Change Language')}
            </div>

            <a href="#" onClick={closeMyAccountWindow} className="MyAccountWindow__closeButton"><img src={closeIcon} alt="Close" /></a>
          </li>
        </ul>
        <div id="aux4" className="hidden">Not name</div>
        <input type="text" aria-labelledby="aux4" autoFocus className="hidden"/>
        <ul className="MyAccountWindow__list">
          {LANGUAGES.map(renderLanguage)}
        </ul>
      </>
    );
  }

  private renderFontsChangeScreen = () => {
    const { uiStore, closeMyAccountWindow } = this.props;
    const fontsStore = [
      {
        name: '100%',
        classname: 'zoom100'
      },
      {
        name: '150%',
        classname: 'zoom150'
      },
      {
        name: '200%',
        classname: 'zoom200'
      }
    ];
    const setCurrentFonts = (font: string) => {
      if (uiStore!.currentFont === font) {
        return null;
      }
      uiStore!.setCurrentFont(font);
      document.querySelectorAll('.App')[0].classList.remove('zoom100');
      document.querySelectorAll('.App')[0].classList.remove('zoom150');
      document.querySelectorAll('.App')[0].classList.remove('zoom200');
      document.querySelectorAll('.App')[0].classList.add(font);
    };
    const renderFont = (fontstore: { name: string, classname: string }) => (
      <li
        className="MyAccountWindow__item"
        onClick={() => setCurrentFonts(fontstore.classname)}
      >
        <a
          href="#"
          className={classnames('MyAccountWindow__itemText', { MyAccountWindow__itemText_selected: uiStore!.currentFont === fontstore.classname })}
        >
          {fontstore.name}
        </a>
      </li>
    );

    return (
      <>
        <ul className="MyAccountWindow__list MyAccountWindow__list_separated">
          <li className="MyAccountWindow__item MyAccountWindow__item_centered">
            <img className="MyAccountWindow__backButton" src={backIcon} alt={intl.get('header.Back')} onClick={this.goToMainScreen} />

            <div className="MyAccountWindow__itemText MyAccountWindow__itemText_selected">
              {intl.get('header.change_font_size')}
            </div>

            <a href="#" className="MyAccountWindow__closeButton" onClick={closeMyAccountWindow}><img src={closeIcon} alt="Close" /></a>
          </li>
        </ul>
        <div id="aux3" className="hidden">Not name</div>
        <input type="text" aria-labelledby="aux3" autoFocus className="hidden"/>
        <ul className="MyAccountWindow__list">
          {fontsStore.map(renderFont)}
        </ul>
      </>
    );
  }

  private nodeRefFor(key: string): React.RefObject<HTMLDivElement | null> {
    if (!this.nodeRefs.has(key)) {
      this.nodeRefs.set(key, createRef<HTMLDivElement>());
    }

    return this.nodeRefs.get(key)!;
  }

  private renderAnimationWrapper = (className: string, children: ReactNode) => {
    const key = String(this.state.currentScreen);
    const nodeRef = this.nodeRefFor(key);

    return (
      <CSSTransition
        key={key}
        classNames={className}
        timeout={ANIMATION_TIMEOUT}
        nodeRef={nodeRef}
        onEntering={() => this.handleElementEntering(nodeRef.current)}
        onEntered={this.handleElementEntered}
        onExit={this.handleElementExit}
      >
        <div className="MyAccountWindow__container" ref={nodeRef}>
          {children}
        </div>
      </CSSTransition>
    );
  }

  public handleKeyboardControl = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      this.goToMainScreen();
    }
  }

  public componentDidMount() {
    document.addEventListener('keyup', this.handleKeyboardControl);
  }

  public componentWillUnmount() {
    document.removeEventListener('keyup', this.handleKeyboardControl);
  }

  public render() {
    const { currentScreen } = this.state;
    const localeScreen = this.renderAnimationWrapper('MyAccountWindow__secondaryContainer_animated', this.renderLanguageChangeScreen());
    const fontsScreen = this.renderAnimationWrapper('MyAccountWindow__secondaryContainer_animated', this.renderFontsChangeScreen());
    const mainScreen = this.renderAnimationWrapper('MyAccountWindow__container_animated', this.renderMainScreen());

    return (
      <div className="MyAccountWindow" ref={this.containerRef}>
        <div id="aux2" className="hidden">Not name</div>
        <input type="text" aria-labelledby="aux2" autoFocus className="hidden"/>
        <TransitionGroup>
          {currentScreen === Screen.MAIN && mainScreen}
          {currentScreen === Screen.LOCALE && localeScreen}
          {currentScreen === Screen.FONTS && fontsScreen}
        </TransitionGroup>
      </div>
    );
  }
}

class MyAccountWindowWrapperComponent extends Component<MyAccountWindowProps> {
  private clickOutsideRef = createRef<HTMLDivElement>();

  public handleClickOutside = (e: SyntheticEvent) => this.props.closeMyAccountWindow(e);

  /**
   * onClickOutside resolves the element to test clicks against with this.
   *
   * It has to name the ref that render() actually hands down, not this wrapper's own. When
   * AppHeader supplies an outerRef — which it always does, for its CSSTransition — the
   * wrapper's own ref is attached to nothing and reads back null, and an outside click was
   * silently ignored. Broken since the setClickOutsideRef sweep; before that the library
   * found the node with findDOMNode and did not care which ref held it.
   */
  public setClickOutsideRef = () => this.nodeRef.current;

  public render() {
    return <MyAccountWindow {...this.props} outerRef={this.nodeRef}/>;
  }

  /** The ref render() passes down, so both this and the node stay in agreement. */
  private get nodeRef() {
    return this.props.outerRef || this.clickOutsideRef;
  }
}

export const MyAccountWindowWrapper = onClickOutside(MyAccountWindowWrapperComponent);
